import React, { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { Row } from 'reactstrap';
import { AttendeeStats, IdentityStats } from '../../../shared/stats';
import { InfoCard, PageHeader, PermissionBoundary, UserStateComponent } from '../../components';
import { UserListModal } from '../../components/UserListModal';
import { useTranslation } from '../../translations';
import { Fetcher, isAccessError, useConvention, useFetcher } from '../../utils';

const AttendeeStatsComponent: FC<{ readonly stats: AttendeeStats }> = ({ stats }) => {
  const { ts } = useTranslation();
  const [modal, setModal] = useState<'pendingReview' | null>(null);

  const paidStats = stats.paid.map(({ name, count }) => (
    <InfoCard
      className="mb-10"
      heading={
        <>
          {name} ({ts('common.paid')})
        </>
      }
      icon="people_outline"
      id={`${name}PaidStats`}
      key={name}
      largeText
      status="info"
      text={count}
    />
  ));

  return (
    <>
      {modal && (
        <UserListModal
          close={() => {
            setModal(null);
          }}
          title="pending_accounts_review"
          userList={stats.pendingReviewUserIds}
        />
      )}
      <InfoCard
        className="mb-10"
        heading="common.total_registrations"
        icon="people"
        id="attendeeRegistrationStats"
        largeText
        status="info"
        text={stats.total}
      />
      <InfoCard
        className="mb-10"
        heading="common.total_paid_registrations"
        icon="people"
        id="attendeePaidRegistrationStats"
        largeText
        status="info"
        text={stats.totalPaid}
      />
      {stats.printed > 0 && (
        <InfoCard
          className="mb-10"
          heading="badges_printed"
          icon="print"
          id="badgesPrinted"
          largeText
          status="info"
          text={stats.printed}
        />
      )}
      <PermissionBoundary inline requiredPermissions="user:note:delete">
        {stats.pendingReviewCount > 0 && (
          <InfoCard
            className="mb-10"
            heading={
              <a
                onClick={() => {
                  setModal('pendingReview');
                }}
                style={{ cursor: 'pointer' }}
              >
                {ts('pending_account_review')}
              </a>
            }
            icon="key"
            id="pendingReview"
            largeText
            status="info"
            text={stats.pendingReviewCount}
          />
        )}
      </PermissionBoundary>
      <PermissionBoundary inline requiredPermissions="refund:create">
        {stats.pendingRefundRequests > 0 && (
          <InfoCard
            className="mb-10"
            heading={
              <Link to="/housekeeping/attendees/refund">
                {ts('common.pending')} {ts('common.refund_requests')}
              </Link>
            }
            icon="payments"
            id="pendingRefundRequests"
            largeText
            status="info"
            text={stats.pendingRefundRequests}
          />
        )}
      </PermissionBoundary>
      {...paidStats}
    </>
  );
};

const IdentityStatsComponent: FC<{ readonly stats: IdentityStats }> = ({ stats }) => {
  const { ts } = useTranslation();
  const identityStats = stats.status.map(({ name, count }) => (
    <InfoCard
      className="mb-10"
      heading={
        <>
          {ts('common.identity')}: {name}
        </>
      }
      icon="people_outline"
      id={`${name}IdentityStats`}
      key={name}
      largeText
      status="info"
      text={count}
    />
  ));

  return (
    <>
      <InfoCard
        className="mb-10"
        heading="identity_verifications_left"
        icon="people"
        id="identityLeft"
        largeText
        status="info"
        text={stats.left}
      />
      {...identityStats}
    </>
  );
};

export const HousekeepingHome: FC = () => {
  const { ts } = useTranslation();
  const convention = useConvention();
  const attendeeStatsFetcher = useFetcher(async () => await api.getAttendeeStats(), []);
  const identityStatsFetcher = useFetcher(async () => await api.getIdentityStats(), []);
  return (
    <UserStateComponent>
      <PageHeader>{ts('common.dashboard')}</PageHeader>
      <Row className="justify-content-center" id="dashboard">
        {attendeeStatsFetcher.data ? (
          <AttendeeStatsComponent stats={attendeeStatsFetcher.data} />
        ) : isAccessError(attendeeStatsFetcher.error) ? (
          <InfoCard
            heading="no_stats_available"
            icon="show_chart"
            status="warning"
            text={[ts('you_dont_have_access_to_view_any_statistics_information')]}
          />
        ) : (
          <Fetcher inline result={attendeeStatsFetcher} />
        )}

        {convention.identityVerification !== 'none' && (
          <>
            <hr />
            {identityStatsFetcher.data ? (
              <IdentityStatsComponent stats={identityStatsFetcher.data} />
            ) : (
              <Fetcher inline result={identityStatsFetcher} />
            )}
          </>
        )}
      </Row>
      <hr />
    </UserStateComponent>
  );
};
